import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Box,
} from "@material-ui/core";

interface CancellationResultData {
  reference?: string;
  action?: string;
  status?: string;
  tillo_attempted?: boolean;
  tillo_success?: boolean | null;
  tillo_message?: string;
  user_cancellation_done?: boolean;
  refund_amount_status?: string;
  transfer_rewards_back_status?: string;
  payment_source?: string;
  email_sent_to_tillo?: boolean;
  exception_type?: string;
  [key: string]: any;
}

export interface CancellationResult {
  success: boolean;
  message: string;
  data?: CancellationResultData;
}

const STATUS_DISPLAY_MAP: Record<string, string> = {
  pending: "Pending",
  completed: "Completed",
  tillo_completed: "Pending",
  declined: "Declined",
  tillo_failed: "Tillo Failed",
  cancel_to_user_failed: "Refund Failed",
  refund_failed: "Refund Failed",
};

const getStatusStyle = (status: string) => {
  if (status === "completed" || status === "tillo_completed") return { backgroundColor: "#d4edda", color: "#155724" };
  if (["tillo_failed", "cancel_to_user_failed", "refund_failed", "declined"].includes(status)) return { backgroundColor: "#f8d7da", color: "#721c24" };
  return { backgroundColor: "#fff3cd", color: "#856404" };
};

export const CancellationResultCard: React.FC<{ result: CancellationResult }> = ({ result }) => {
  return (
    <Box
      style={{
        marginTop: 16,
        border: `2px solid ${result.success ? "#28a745" : "#dc3545"}`,
        borderRadius: 4,
      }}
    >
      <Box
        style={{
          padding: 12,
          backgroundColor: result.success ? "#d4edda" : "#f8d7da",
          borderBottom: `1px solid ${result.success ? "#c3e6cb" : "#f5c6cb"}`,
        }}
      >
        <Typography variant="h6" style={{ color: result.success ? "#155724" : "#721c24" }}>
          {result.success ? "Cancellation Result" : "Cancellation Failed"}
        </Typography>
        <Typography variant="body2" style={{ color: result.success ? "#155724" : "#721c24", marginTop: 4 }}>
          {result.message}
        </Typography>
      </Box>

      {result.data && (
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell style={{ fontWeight: 600, width: "35%", backgroundColor: "#f8f9fa" }}>Reference</TableCell>
              <TableCell>{result.data.reference || "N/A"}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ fontWeight: 600, backgroundColor: "#f8f9fa" }}>Action Taken</TableCell>
              <TableCell>{result.data.action || "N/A"}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ fontWeight: 600, backgroundColor: "#f8f9fa" }}>Status</TableCell>
              <TableCell>
                <span style={{
                  padding: "2px 8px",
                  borderRadius: 4,
                  ...getStatusStyle(result.data.status || "")
                }}>
                  {STATUS_DISPLAY_MAP[result.data.status || ""] || result.data.status || "N/A"}
                </span>
              </TableCell>
            </TableRow>

            {result.data.tillo_attempted !== undefined && (
              <>
                <TableRow>
                  <TableCell colSpan={2} style={{ fontWeight: 700, backgroundColor: "#e9ecef", paddingTop: 12, paddingBottom: 12 }}>
                    Tillo Cancellation Status
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ fontWeight: 600, backgroundColor: "#f8f9fa" }}>Tillo Attempted</TableCell>
                  <TableCell>{result.data.tillo_attempted ? "Yes" : "No"}</TableCell>
                </TableRow>
                {result.data.tillo_attempted && (
                  <>
                    <TableRow>
                      <TableCell style={{ fontWeight: 600, backgroundColor: "#f8f9fa" }}>Tillo Success</TableCell>
                      <TableCell>
                        {result.data.tillo_success ? (
                          <span style={{ color: "#28a745", fontWeight: 600 }}>Success</span>
                        ) : result.data.tillo_success === false ? (
                          <span style={{ color: "#dc3545", fontWeight: 600 }}>Failed</span>
                        ) : "N/A"}
                      </TableCell>
                    </TableRow>
                    {result.data.tillo_message && (
                      <TableRow>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f8f9fa" }}>Tillo Message</TableCell>
                        <TableCell>{result.data.tillo_message}</TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </>
            )}

            {result.data.user_cancellation_done !== undefined && (
              <>
                <TableRow>
                  <TableCell colSpan={2} style={{ fontWeight: 700, backgroundColor: "#e9ecef", paddingTop: 12, paddingBottom: 12 }}>
                    User Refund Status
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ fontWeight: 600, backgroundColor: "#f8f9fa" }}>User Cancellation Done</TableCell>
                  <TableCell>
                    {result.data.user_cancellation_done ? (
                      <span style={{ color: "#28a745", fontWeight: 600 }}>Yes</span>
                    ) : (
                      <span style={{ color: "#6c757d" }}>No</span>
                    )}
                  </TableCell>
                </TableRow>
                {result.data.refund_amount_status && (
                  <TableRow>
                    <TableCell style={{ fontWeight: 600, backgroundColor: "#f8f9fa" }}>Refund Amount Status</TableCell>
                    <TableCell>
                      {result.data.refund_amount_status.startsWith("Failed") ? (
                        <span style={{ color: "#dc3545", fontWeight: 600 }}>{result.data.refund_amount_status}</span>
                      ) : (
                        <span style={{ color: "#28a745", fontWeight: 600 }}>{result.data.refund_amount_status}</span>
                      )}
                    </TableCell>
                  </TableRow>
                )}
                {result.data.transfer_rewards_back_status && (
                  <TableRow>
                    <TableCell style={{ fontWeight: 600, backgroundColor: "#f8f9fa" }}>Rewards Cancellation Status</TableCell>
                    <TableCell>
                      {result.data.transfer_rewards_back_status.startsWith("Failed") ? (
                        <span style={{ color: "#dc3545", fontWeight: 600 }}>{result.data.transfer_rewards_back_status}</span>
                      ) : result.data.transfer_rewards_back_status === "Skipped - refund failed" ? (
                        <span style={{ color: "#6c757d" }}>{result.data.transfer_rewards_back_status}</span>
                      ) : (
                        <span style={{ color: "#28a745", fontWeight: 600 }}>{result.data.transfer_rewards_back_status}</span>
                      )}
                    </TableCell>
                  </TableRow>
                )}
                {result.data.payment_source && (
                  <TableRow>
                    <TableCell style={{ fontWeight: 600, backgroundColor: "#f8f9fa" }}>Payment Source</TableCell>
                    <TableCell>{result.data.payment_source}</TableCell>
                  </TableRow>
                )}
              </>
            )}

            {result.data.email_sent_to_tillo && (
              <>
                <TableRow>
                  <TableCell colSpan={2} style={{ fontWeight: 700, backgroundColor: "#e9ecef", paddingTop: 12, paddingBottom: 12 }}>
                    Email Notification
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ fontWeight: 600, backgroundColor: "#f8f9fa" }}>Email Sent to Tillo</TableCell>
                  <TableCell>
                    <span style={{ color: "#28a745", fontWeight: 600 }}>Yes</span>
                  </TableCell>
                </TableRow>
                {result.data.exception_type && (
                  <TableRow>
                    <TableCell style={{ fontWeight: 600, backgroundColor: "#f8f9fa" }}>Exception Type</TableCell>
                    <TableCell>{result.data.exception_type}</TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};
